import { useContext, Children, cloneElement } from 'react';
import type { ReactElement, ComponentPropsWithRef, JSX } from 'react';
import { EditableTextContext } from './EditableTextRoot';

export type EditableTextTriggerProps = {
  /**
   * A trigger element, typically a [Button]() that will enter editing mode when clicked.
   */
  children: ReactElement;
};

export const EditableTextTrigger = ({
  children,
  ...props
}: EditableTextTriggerProps): JSX.Element | null => {
  const context = useContext(EditableTextContext);
  if (!context) {
    throw new Error('EditableTextTrigger must be used within an EditableTextRoot context');
  }
  const { setIsEditing, isEditing } = context;

  const handleClick = () => {
    setIsEditing(true);
  };

  const onlyChild = Children.only(children);

  const triggerProps: Partial<ComponentPropsWithRef<'button'>> & {
    'data-wui-editable-text-trigger'?: boolean;
  } = {
    onClick: handleClick,
    'data-wui-editable-text-trigger': true,
    ...props,
  };

  if (isEditing) {
    return null;
  }

  return cloneElement(onlyChild, triggerProps);
};
